import { useTranslation } from 'react-i18next';

import User, { UserTypeEnum } from '@/models/User';
import DataStudentsTable from './DataStudentsTable';
import { StudentHeader } from '@/utils/HeaderTypes';
import Header from '@/components/ui/custom/table/Header';
import Text from '@/components/ui/custom/dataDisplay/Text';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { LoadingIcon } from '@/components/icons';
import { ListStudentsFilters } from '@/data/services/userServices';
import { FilterProps } from '@/models/FilterConfig';
import { isSuperAdmin, isTeacher, isUnitAdmin } from '@/functions/auth';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import { createMultipleUnitFilter } from '@/utils/filterFactory';
import { useDynamicParams } from '@/data/hook/useDynamicParams';
import Filter from '@/components/filter/Filter';
import { Fields, searchParamsToObject } from '@/utils/searchParamsToObject';
import { EnrollmentStatusEnum } from '@/models/Enrollment';
import { useFilterLabelTranslated } from '@/components/filter/Filter.hooks';
import { studentsQuerykeys } from '@/data/services/querykeys';
import useListService from '@/data/hook/useListService';
import PaginationComponent from '@/pages/admin/PaginationComponent';
import { StudentTypeEnum } from '@/enums/StudentTypeEnum';
import { MANAGEMENT_PAGE_SIZE as pageSize } from '@/constants';

type ListStudentsProps = {
  user: User;
};

const sanitizeFilters = (filters: ListStudentsFilters) => {
  return {
    ...filters,
    idIn: undefined,
  };
};

export default function ListStudents(props: ListStudentsProps) {
  const { user } = props;

  const { translate } = useFilterLabelTranslated();

  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { t: tManage } = useTranslation('translation', {
    keyPrefix: 'manageStudent',
  });

  const unitIds = getAuthorizedUnits(user) ?? [];

  const isUnitStaff = isUnitAdmin(user.userType) || isTeacher(user.userType);

  const isViewUnits =
    isSuperAdmin(user.userType) || (isUnitStaff && unitIds.length > 1);

  const titles: StudentHeader = {
    name: {
      title: t('name'),
      size: 24,
    },
    klass: {
      title: t('klass'),
      size: 18,
      roles: [UserTypeEnum.TEACHER],
    },
    email: {
      title: t('email'),
      size: 24,
    },

    unit: {
      title: t('unit'),
      size: 10,
      blockRender: !isViewUnits,
      blockCondition: !isViewUnits,
    },

    lastAccess: {
      title: t('lastLogin'),
      size: 16,
    },
    status: {
      title: t('status'),
      size: 10,
    },
    actions: {
      title: t('actions'),
      size: 23,
    },
  };

  const changePages = (numberOfPage: string) => {
    updateParams({ pageNumber: Number(numberOfPage) });
  };

  const configs: FilterProps[] = [
    {
      type: 'search',
      label: t('search'),
      debounce: true,
      name: 'search',
      placeholder: t('research'),
    },
    createMultipleUnitFilter({
      hide: !isViewUnits,
      filters: { idIn: getAuthorizedUnits(user)?.toString() },
    }),
    {
      type: 'dualArrowOrdering',
      label: t('lastLogin'),
      name: 'ordering',
      options: [
        {
          label: t('ascending'),
          value: 'last_login',
        },
        {
          label: t('descending'),
          value: '-last_login',
        },
      ],
    },
    {
      name: 'enrollmentStatus',
      label: t('status'),
      type: 'multiple',
      mode: 'static',
      options: Object.values(EnrollmentStatusEnum).map(enrollmentStatus => ({
        label: translate('enrollmentStatus', enrollmentStatus),
        value: enrollmentStatus,
      })),
    },
    {
      name: 'studentType',
      label: t('enrollmentType'),
      type: 'multiple',
      mode: 'static',
      options: Object.values(StudentTypeEnum).map(studentType => ({
        label: translate('studentType', studentType),
        value: studentType,
      })),
    },
  ];

  const { params, updateParams } = useDynamicParams<ListStudentsFilters>();

  const hasParams = [...params.keys()].length !== 0;

  const configFields: Fields<ListStudentsFilters> = {
    unitId: { isArray: true, type: 'number' },
    ordering: { type: 'string' },
    search: { type: 'string' },
    enrollmentStatus: { isArray: true, type: 'string' },
    studentType: { isArray: true, type: 'string' },
    idIn: { isArray: true, type: 'number' },
  };

  const filter = searchParamsToObject<ListStudentsFilters>(
    params,
    configFields,
  );

  const sanitizedFilter = sanitizeFilters(filter);

  const {
    results: students,
    meta,
    isInitialLoading,
    isPreviousData,
  } = useListService({
    ...studentsQuerykeys.list({ ...filter, pageSize }),
  });

  const pagination = {
    params,
    amountOf: meta?.pagination.count || 0,
    amountPages: meta?.pagination.pages || 1,
    paramsPage: filter.pageNumber || 1,
    isLoading: isInitialLoading || isPreviousData,
    changePages,
  };

  const loading = isInitialLoading || isPreviousData;

  return (
    <div className="flex flex-col gap-4">
      <Filter
        configs={configs}
        filter={sanitizedFilter}
        onChange={filter => updateParams({ ...filter, pageNumber: 1 })}
        className={{ base: 'xl:flex-row justify-between' }}
      />
      <table className="flex flex-col gap-y-2.5">
        <Header headers={Object.values(titles)} />
        <ConditionalRenderer condition={!loading}>
          <DataStudentsTable titles={titles} students={students} />
        </ConditionalRenderer>
      </table>
      <ConditionalRenderer condition={!students.length && !loading}>
        <Text
          text={
            hasParams
              ? tManage('dataUserTable.searchNotFound')
              : tManage('dataUserTable.noStudents')
          }
          className="flex justify-center mt-5"
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={loading}>
        <div className="flex w-full h-[518px] justify-center items-center p-3.5">
          <LoadingIcon className="text-primary/40 w-10 h-10" />
        </div>
      </ConditionalRenderer>
      <PaginationComponent
        {...pagination}
        changePages={changePages}
        managerType="students"
      />
    </div>
  );
}
